/* eslint-disable no-shadow */
import Vue from 'vue';
// eslint-disable-next-line import/no-cycle
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import {
  ValidationError, PMSError, HttpError,
} from '@/errors';

const endpoint = `${apiEndpoint}/inventory/promotions`;

const state = {
  promotions: [],
  channels: [],
  products: [],
  loaded: false,
  pmsError: null,
  validationError: null,
  updatePending: false,
  error: null,
};

const getters = {
  promotions: (state) => state.promotions,
  channels: (state) => state.channels,
  products: (state) => state.products,
  updatePending: (state) => state.updatePending,
  error: (state) => state.error,
  validationError: (state) => state.validationError,
};

const mutations = {
  clearErrors(state) {
    state.validationError = null;
  },
  onLoad() {
    state.loaded = true;
  },
  beforeRequest() {
    state.validationError = null;
    state.updatePending = true;
    state.loaded = true;
  },
  requestComplete() {
    state.updatePending = false;
    state.loaded = false;
  },
  error(state, error) {
    state.pmsError = error;
  },
  validationError(state, error) {
    state.validationError = error;
    state.pending = false;
  },
  fetched(state, promotions) {
    state.promotions = promotions?.data;
  },
  fetchedConnectedChannels(state, channels) {
    state.channels = channels?.data;
  },
  fetchedProducts(state, products) {
    state.products = products?.data;
    state.loaded = false;
  },
  updated(state, promotion) {
    const idx = state.promotions.findIndex((p) => p.id === promotion.id);
    if (idx !== -1) {
      Vue.set(state.promotions, idx, JSON.parse(JSON.stringify(promotion)));
    }
  },
  deleted(state, id) {
    const idx = state.promotions.findIndex((promotion) => Number(promotion.id) === id);
    if (idx !== -1) {
      state.promotions.splice(idx, 1);
    }
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  throw err;
}

const actions = {
  async onLoad({ commit }) {
    commit('onLoad');
  },

  async getPromotions({ commit }) {
    commit('beforeRequest');
    try {
      const { data } = await ApiService.get(`${endpoint}`);
      commit('fetched', data);
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      commit('error', error);
      handleApiError(commit, error);
    }
  },

  async getProducts({ commit }) {
    commit('onLoad');
    try {
      const { data } = await ApiService.get(`${endpoint}/products-data`);
      commit('fetchedProducts', data);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },

  async editPromotion({ commit }, promotion) {
    commit('beforeRequest');
    try {
      await ApiService.put(`${endpoint}`, promotion);
      commit('updated', promotion);
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
  },

  async deletePromotion({ commit }, promotionId) {
    commit('beforeRequest');
    try {
      await ApiService.delete(`${endpoint}/${promotionId}`);
      commit('deleted', promotionId);
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
  },

  async addPromotion({ commit }, promotion) {
    commit('beforeRequest');
    try {
      const { data } = await ApiService.post(`${endpoint}`, promotion);
      commit('fetched', data);
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
